/* eslint-disable react/no-multi-comp */
import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { Col, Row, Select, Button, Tooltip } from 'antd';
import { UploadOutlined, CloseOutlined, FilePdfOutlined, EyeOutlined } from '@ant-design/icons';
import { useFormikContext } from 'formik';
import styled from 'styled-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import dayjs from 'dayjs';
import { FormInput, CustomInput } from 'atoms';
import { DatePickers } from 'utils/commonStyled';
import { docimgpdffiletypetoShow } from 'utils/config';
import {
	BILLING_TYPE_FIXED,
	BILLING_TYPE_FIXED_VARIABLE,
	BILLING_TYPE_VARIABLE,
	BILLING_TYPE_FIXED_PER_DAY,
	temperatureOptions,
	billingTypeOptions,
	weekOffDaysOptions,
} from './ClientContractFormV2.constants';
import { subBillingTypeOptions } from './PerOrderRatesField';

const StyledDatePicker = styled(DatePickers)`
	width: 100%;
`;

const UploadedFileContainer = styled.div`
	display: flex;
	align-items: center;
	padding: 12px;
	margin-top: 12px;
	background: #f5f5f5;
	border: 1px solid #d9d9d9;
	border-radius: 4px;
	gap: 12px;
`;

const FileIcon = styled.div`
	font-size: 24px;
	color: #ff4d4f;
`;

const FileInfo = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const FileName = styled.div`
	font-size: 14px;
	color: #262626;
	font-weight: 500;
`;

const FileSize = styled.div`
	font-size: 12px;
	color: #8c8c8c;
	margin-top: 2px;
`;

const RemoveButton = styled(Button)`
	&.ant-btn {
		border: none;
		padding: 0;
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #8c8c8c;

		&:hover {
			color: #ff4d4f;
			background: transparent;
		}
	}
`;

const ViewButton = styled(Button)`
	&.ant-btn {
		border: none;
		padding: 0;
		width: 24px;
		height: 24px;
		display: flex;
		align-items: center;
		justify-content: center;
		color: #8c8c8c;

		&:hover {
			color: #1890ff;
			background: transparent;
		}
	}
`;

const FileActions = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
`;

// Constants for Select component messages
const LOADING_MESSAGE = 'Loading...';
const NO_RESULTS_MESSAGE = {
	clients: 'No clients found',
	projects: 'No projects found',
	vehicleTypes: 'No vehicle types found',
	routes: 'No routes found',
};

/**
 * Common Fields - Client, Project, Temperature, Vehicle Type, Capacity
 */
export function BasicInfoFields({
	clientsData,
	projectData,
	vehicleData,
	onProjectChange,
	onVehicleTypeChange,
	onSearchClients,
	onSearchProjects,
	onVehicleTypeFetch,
	loadingClients,
	loadingProjects,
	loadingVehicleTypes,
	isViewMode = false,
	isEditFlow = false,
}) {
	const { setFieldValue, values } = useFormikContext();

	// Use vehicleData directly (it's already filtered by temperature controlled from parent)
	const filteredVehicleData = vehicleData || [];

	return (
		<>
			{/* Row 1: Client and Project */}
			<Row gutter={16}>
				<Col lg={12}>
					<FormInput name="client_id" label="Select Client*">
						<Select
							name="client_id"
							showSearch
							placeholder="Select"
							options={clientsData}
							filterOption={false}
							onSearch={onSearchClients}
							loading={loadingClients}
							value={values.client_id}
							onChange={(value) => {
								setFieldValue('client_id', value || undefined);
								// Clear dependent fields when client changes
								setFieldValue('project_id', undefined);
								setFieldValue('route_id', undefined);
								setFieldValue('vehicle_type_id', undefined);
								setFieldValue('capacity_tonnes', '');
								// Fetch projects for the selected client
								if (value && onSearchProjects) {
									onSearchProjects('', value);
								}
							}}
							disabled={isViewMode || isEditFlow}
							notFoundContent={
								loadingClients ? LOADING_MESSAGE : NO_RESULTS_MESSAGE.clients
							}
						/>
					</FormInput>
				</Col>
				<Col lg={12}>
					<FormInput name="project_id" label="Select Project*">
						<Select
							name="project_id"
							showSearch
							placeholder="Select"
							options={projectData}
							filterOption={false}
							onSearch={(searchTerm) => {
								if (onSearchProjects) {
									// Always pass client_id when searching projects
									onSearchProjects(searchTerm, values.client_id);
								}
							}}
							loading={loadingProjects}
							value={values.project_id}
							onChange={(value) => {
								const projectId = value ? Number(value) : undefined;
								setFieldValue('project_id', projectId);
								setFieldValue('route_id', undefined); // Clear route on project change
								if (onProjectChange) {
									onProjectChange(projectId);
								}
							}}
							disabled={!values.client_id || isViewMode || isEditFlow}
							notFoundContent={
								loadingProjects ? LOADING_MESSAGE : NO_RESULTS_MESSAGE.projects
							}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Row 2: Temperature Controlled and Vehicle Type with Capacity */}
			<Row gutter={16}>
				<Col lg={12}>
					<FormInput name="temperature_controlled" label="Temperature Controlled*">
						<Select
							name="temperature_controlled"
							showSearch
							placeholder="Select"
							options={temperatureOptions}
							value={values.temperature_controlled}
							onChange={(value) => {
								setFieldValue('temperature_controlled', value);
								// Clear vehicle type, capacity, and route when temperature controlled changes
								setFieldValue('vehicle_type_id', undefined);
								setFieldValue('capacity_tonnes', '');
								setFieldValue('route_id', undefined);
								// Fetch vehicle types based on temperature controlled
								if (onVehicleTypeFetch) {
									onVehicleTypeFetch(value);
								}
							}}
							disabled={isViewMode || isEditFlow}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
						/>
					</FormInput>
				</Col>
				<Col lg={8}>
					<FormInput name="vehicle_type_id" label="Vehicle Type*">
						<Select
							name="vehicle_type_id"
							showSearch
							placeholder={
								values.temperature_controlled === undefined ||
								values.temperature_controlled === null
									? 'Select Temperature Controlled first'
									: 'Select'
							}
							options={filteredVehicleData}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
							loading={loadingVehicleTypes}
							value={values.vehicle_type_id}
							disabled={
								values.temperature_controlled === undefined ||
								values.temperature_controlled === null ||
								isViewMode ||
								isEditFlow
							}
							onChange={(value) => {
								const vehicleTypeId = value ? Number(value) : undefined;
								setFieldValue('vehicle_type_id', vehicleTypeId);
								setFieldValue('route_id', undefined); // Clear route on vehicle type change

								// Auto-fill capacity from selected vehicle type
								if (value) {
									const selectedVehicle = filteredVehicleData.find(
										(v) => v.value === value
									);
									if (selectedVehicle?.capacity_tonnes) {
										setFieldValue(
											'capacity_tonnes',
											selectedVehicle.capacity_tonnes
										);
									}
								} else {
									// Clear capacity if vehicle type is cleared
									setFieldValue('capacity_tonnes', '');
								}

								if (onVehicleTypeChange) {
									onVehicleTypeChange(vehicleTypeId);
								}
							}}
							notFoundContent={
								loadingVehicleTypes
									? LOADING_MESSAGE
									: NO_RESULTS_MESSAGE.vehicleTypes
							}
						/>
					</FormInput>
				</Col>
				<Col lg={4}>
					<FormInput name="capacity_tonnes" label="Capacity(in tones)">
						<CustomInput
							name="capacity_tonnes"
							placeholder="Capacity in tones"
							disabled
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Row 3: Billing Type and Contract/Expiry Dates */}
			<Row gutter={16}>
				<Col lg={12}>
					<FormInput name="billing_type" label="Billing Type*">
						<Select
							name="billing_type"
							showSearch
							placeholder="Select"
							options={billingTypeOptions}
							value={values.billing_type}
							onChange={(value) => {
								setFieldValue('billing_type', value);
								setFieldValue('billing_subtype', undefined);
							}}
							disabled={isViewMode || isEditFlow}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="contract_date" label="Contract Date">
						<StyledDatePicker
							name="contract_date"
							placeholder="Choose Date"
							format="DD-MM-YYYY"
							disabled={isViewMode}
							onChange={(date) => {
								setFieldValue('contract_date', date);
								// Clear expiry date if it's before the new contract date
								if (
									values.expiry_date &&
									date &&
									dayjs(values.expiry_date).isBefore(date)
								) {
									setFieldValue('expiry_date', undefined);
								}
							}}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="expiry_date" label="Expiry Date">
						<StyledDatePicker
							name="expiry_date"
							placeholder="Choose Date"
							format="DD-MM-YYYY"
							disabled={!values.contract_date || isViewMode}
							disabledDate={(current) => {
								if (!values.contract_date) return true;
								return (
									current && current <= dayjs(values.contract_date).endOf('day')
								);
							}}
						/>
					</FormInput>
				</Col>
			</Row>
			{(values.billing_type === BILLING_TYPE_VARIABLE ||
				values.billing_type === BILLING_TYPE_FIXED_VARIABLE ||
				values.billing_type === BILLING_TYPE_FIXED_PER_DAY) && (
				<Row gutter={16}>
					<Col lg={12}>
						<FormInput name="billing_subtype" label="Sub Billing Type*">
							<Select
								name="billing_subtype"
								placeholder="Select"
								options={subBillingTypeOptions}
								value={values.billing_subtype}
								onChange={(value) => setFieldValue('billing_subtype', value)}
								disabled={isViewMode || isEditFlow}
							/>
						</FormInput>
					</Col>
				</Row>
			)}
		</>
	);
}

BasicInfoFields.propTypes = {
	clientsData: PropTypes.array.isRequired,
	projectData: PropTypes.array.isRequired,
	vehicleData: PropTypes.array.isRequired,
	onProjectChange: PropTypes.func,
	onVehicleTypeChange: PropTypes.func,
	onSearchClients: PropTypes.func,
	onSearchProjects: PropTypes.func,
	onVehicleTypeFetch: PropTypes.func,
	loadingClients: PropTypes.bool,
	loadingProjects: PropTypes.bool,
	loadingVehicleTypes: PropTypes.bool,
	isViewMode: PropTypes.bool,
	isEditFlow: PropTypes.bool,
};

/**
 * Fixed and Fixed + Variable Billing Type Fields
 */
export function FixedBillingFields({ isViewMode = false, showExtraCharges = true }) {
	return (
		<>
			{/* Row: Fixed Hours, Threshold KM Day, Threshold KM Month, Vehicle Cost */}
			<Row gutter={16}>
				<Col lg={6}>
					<FormInput name="fixed_hours_per_day" label="Fixed Hours Per Day">
						<CustomInput
							name="fixed_hours_per_day"
							placeholder="Eg: 12"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="threshold_km_per_day" label="Threshold KM Per Day">
						<CustomInput
							name="threshold_km_per_day"
							placeholder="Eg: 100"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="threshold_km_per_month" label="Threshold KM Per Month">
						<CustomInput
							name="threshold_km_per_month"
							placeholder="Eg: 100"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="vehicle_cost" label="Vehicle Cost">
						<CustomInput
							name="vehicle_cost"
							placeholder="Eg: 12"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Row: Loader, Delivery Associate, Extra Charges trip & hour */}
			<Row gutter={16}>
				<Col lg={6}>
					<FormInput name="loader_cost" label="Loader Cost">
						<CustomInput
							name="loader_cost"
							placeholder="Eg: 12"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				<Col lg={6}>
					<FormInput name="delivery_associate_cost" label="Delivery Associate Cost">
						<CustomInput
							name="delivery_associate_cost"
							placeholder="Eg: 12"
							disabled={isViewMode}
						/>
					</FormInput>
				</Col>
				{showExtraCharges && (
					<>
						<Col lg={6}>
							<FormInput name="extra_charges_per_trip" label="Extra Charges per km">
								<CustomInput
									name="extra_charges_per_trip"
									placeholder="Eg: 12"
									disabled={isViewMode}
								/>
							</FormInput>
						</Col>
						<Col lg={6}>
							<FormInput name="extra_charges_per_hour" label="Extra Charges Per hour">
								<CustomInput
									name="extra_charges_per_hour"
									placeholder="Eg: 12"
									disabled={isViewMode}
								/>
							</FormInput>
						</Col>
					</>
				)}
			</Row>
		</>
	);
}

FixedBillingFields.propTypes = {
	isViewMode: PropTypes.bool,
	showExtraCharges: PropTypes.bool,
};

export function VariableCostFields({ isViewMode = false }) {
	return (
		<Row gutter={16}>
			<Col lg={6}>
				<FormInput name="vehicle_cost" label="Vehicle Cost">
					<CustomInput name="vehicle_cost" placeholder="Eg: 12" disabled={isViewMode} />
				</FormInput>
			</Col>
			<Col lg={6}>
				<FormInput name="loader_cost" label="Loader Cost">
					<CustomInput name="loader_cost" placeholder="Eg: 12" disabled={isViewMode} />
				</FormInput>
			</Col>
			<Col lg={6}>
				<FormInput name="delivery_associate_cost" label="Delivery Associate Cost">
					<CustomInput
						name="delivery_associate_cost"
						placeholder="Eg: 12"
						disabled={isViewMode}
					/>
				</FormInput>
			</Col>
		</Row>
	);
}

VariableCostFields.propTypes = {
	isViewMode: PropTypes.bool,
};

/**
 * Charges per km — Variable billing (both Per Kilometer and Per Order sub-types).
 */
export function ChargesPerKmField({ isViewMode = false }) {
	return (
		<Row gutter={16}>
			<Col lg={6}>
				<FormInput name="charges_per_km" label="Charges per km">
					<CustomInput name="charges_per_km" placeholder="Eg: 12" disabled={isViewMode} />
				</FormInput>
			</Col>
		</Row>
	);
}

ChargesPerKmField.propTypes = {
	isViewMode: PropTypes.bool,
};

/** Fixed Rate Per Day — Fixed Per Day billing only. */
export function FixedRatePerDayField({ isViewMode = false }) {
	return (
		<Row gutter={16}>
			<Col lg={6}>
				<FormInput name="fixed_rate_per_day" label="Fixed Rate Per Day*">
					<CustomInput
						name="fixed_rate_per_day"
						placeholder="Eg: 500"
						disabled={isViewMode}
					/>
				</FormInput>
			</Col>
		</Row>
	);
}

FixedRatePerDayField.propTypes = {
	isViewMode: PropTypes.bool,
};

/**
 * Other Expenses and Route Fields - Common for all billing types
 */
export function AdditionalInfoFields({
	billingType,
	routeData,
	loadingRoutes,
	contractDocumentKey,
	uploadingDocument,
	uploadedFileDetails,
	onContractFileUpload,
	onRemoveDocument,
	onViewDocument,
	isViewMode = false,
	isEditFlow = false,
	fileUploadError,
}) {
	const { setFieldValue, values } = useFormikContext();
	const fileInputRef = useRef(null);

	const handleFileSelect = (e) => {
		e.preventDefault();
		e.stopPropagation();
		console.log('Browse File button clicked');
		console.log('fileInputRef.current:', fileInputRef.current);
		if (fileInputRef.current) {
			fileInputRef.current.click();
			console.log('File input click triggered');
		} else {
			console.error('fileInputRef.current is null');
		}
	};

	const handleFileChange = (event) => {
		console.log('File input changed', event.target.files);
		const file = event.target.files?.[0];
		if (!file) {
			console.log('No file selected');
			return;
		}

		console.log('Selected file:', file.name, file.type, file.size);

		if (onContractFileUpload) {
			console.log('Calling onContractFileUpload with file');
			onContractFileUpload(file);
		} else {
			console.error('onContractFileUpload handler is not provided');
		}

		// Reset input value to allow re-uploading the same file
		if (event.target) {
			event.target.value = '';
		}
	};

	return (
		<>
			{/* Row: Other Expenses - Based on Billing Type */}
			<Row gutter={16}>
				{(billingType === BILLING_TYPE_FIXED ||
					billingType === BILLING_TYPE_FIXED_VARIABLE ||
					billingType === BILLING_TYPE_VARIABLE ||
					billingType === BILLING_TYPE_FIXED_PER_DAY ||
					!billingType) && (
					<>
						<Col lg={6}>
							<FormInput name="other_expenses" label="Other Expenses">
								<CustomInput
									name="other_expenses"
									placeholder="Eg: Fuel, Toll, etc."
									disabled={isViewMode}
								/>
							</FormInput>
						</Col>
						<Col lg={6}>
							<FormInput name="other_other_expenses" label="Other Expenses (₹)">
								<CustomInput
									name="other_other_expenses"
									placeholder="Eg : 500"
									disabled={isViewMode}
								/>
							</FormInput>
						</Col>
					</>
				)}
				<Col lg={12}>
					<FormInput name="monthly_off" label="Weekly Off">
						<Select
							name="monthly_off"
							mode="multiple"
							showSearch
							placeholder="Select Days"
							options={weekOffDaysOptions}
							value={values.monthly_off}
							onChange={(value) => {
								setFieldValue('monthly_off', value);
							}}
							disabled={isViewMode}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
						/>
					</FormInput>
				</Col>
			</Row>

			{/* Row: Route Name and Document Upload */}
			<Row gutter={16}>
				<Col lg={12}>
					<FormInput name="route_id" label="Route Name*">
						<Select
							name="route_id"
							showSearch
							placeholder="Select"
							options={routeData}
							value={values.route_id}
							onChange={(value) => {
								setFieldValue('route_id', value ? Number(value) : undefined);
							}}
							filterOption={(input, option) =>
								(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
							}
							disabled={isViewMode || isEditFlow}
							loading={loadingRoutes}
							notFoundContent={
								loadingRoutes ? LOADING_MESSAGE : NO_RESULTS_MESSAGE.routes
							}
						/>
					</FormInput>
				</Col>
				<Col lg={12}>
					{/* Hidden file input - outside FormInput to avoid interference */}
					<input
						ref={fileInputRef}
						type="file"
						accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/jpg"
						style={{ display: 'none' }}
						onChange={handleFileChange}
					/>
					<FormInput name="contract_document" label="Upload Agreement File*">
						<div>
							<Button
								type="button"
								icon={<UploadOutlined />}
								onClick={handleFileSelect}
								loading={uploadingDocument}
								disabled={uploadingDocument || isViewMode}
								style={{
									width: '100%',
									borderColor: fileUploadError ? '#ff4d4f' : undefined,
								}}
								danger={!!fileUploadError}
							>
								{(() => {
									if (uploadingDocument) return 'Uploading...';
									if (contractDocumentKey) return 'Re-upload Document';
									return 'Browse File';
								})()}
							</Button>

							{/* Display validation error */}
							{fileUploadError && (
								<div
									style={{
										marginTop: 4,
										fontSize: 12,
										color: '#ff4d4f',
									}}
								>
									{fileUploadError}
								</div>
							)}

							{/* Display uploaded file details */}
							{uploadedFileDetails && (
								<UploadedFileContainer>
									<FileIcon>
										<FilePdfOutlined />
									</FileIcon>
									<FileInfo>
										<FileName>{uploadedFileDetails.name}</FileName>
										{uploadedFileDetails.size && (
											<FileSize>
												{(uploadedFileDetails.size / 1024 / 1024).toFixed(
													2
												)}
												mb
											</FileSize>
										)}
									</FileInfo>
									<FileActions>
										{onViewDocument && (
											<Tooltip title="View Document">
												<ViewButton
													type="text"
													icon={<EyeOutlined />}
													onClick={onViewDocument}
													disabled={isViewMode}
												/>
											</Tooltip>
										)}
										<Tooltip title="Remove Document">
											<RemoveButton
												type="text"
												icon={<CloseOutlined />}
												onClick={onRemoveDocument}
												disabled={isViewMode}
											/>
										</Tooltip>
									</FileActions>
								</UploadedFileContainer>
							)}

							<div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
								{docimgpdffiletypetoShow} (Max 8MB)
							</div>
						</div>
					</FormInput>
				</Col>
			</Row>
		</>
	);
}

AdditionalInfoFields.propTypes = {
	billingType: PropTypes.string.isRequired,
	routeData: PropTypes.array.isRequired,
	loadingRoutes: PropTypes.bool,
	contractDocumentKey: PropTypes.string,
	uploadingDocument: PropTypes.bool,
	uploadedFileDetails: PropTypes.shape({
		name: PropTypes.string,
		size: PropTypes.number,
		type: PropTypes.string,
	}),
	onContractFileUpload: PropTypes.func,
	onRemoveDocument: PropTypes.func,
	onViewDocument: PropTypes.func,
	isViewMode: PropTypes.bool,
	isEditFlow: PropTypes.bool,
	fileUploadError: PropTypes.string,
};
